import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import iwalkcvService from '../../services/iwalkcv.service';

interface Ilha {
  id: number;
  nome: string;
  slug: string;
  activo: number;
}

interface FormState {
  nome: string;
  slug: string;
  activo: boolean;
}

const defaultForm: FormState = { nome: '', slug: '', activo: true };

const toSlug = (str: string) =>
  str
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');

const IWalkCVIlhas: React.FC = () => {
  const [ilhas, setIlhas] = useState<Ilha[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<FormState>(defaultForm);
  const [formErrors, setFormErrors] = useState<Partial<FormState>>({});

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [deleting, setDeleting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await iwalkcvService.getIlhas();
      setIlhas(res.data || res);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditingId(null);
    setForm(defaultForm);
    setFormErrors({});
    setDialogOpen(true);
  };

  const openEdit = (ilha: Ilha) => {
    setEditingId(ilha.id);
    setForm({ nome: ilha.nome, slug: ilha.slug, activo: ilha.activo === 1 });
    setFormErrors({});
    setDialogOpen(true);
  };

  const closeDialog = () => { setDialogOpen(false); };

  const validate = (): boolean => {
    const errors: Partial<FormState> = {};
    if (!form.nome.trim()) errors.nome = 'Campo obrigatório';
    if (!form.slug.trim()) errors.slug = 'Campo obrigatório';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const payload = { nome: form.nome, slug: form.slug, activo: form.activo ? 1 : 0 };
      if (editingId !== null) {
        await iwalkcvService.updateIlha(editingId, payload);
      } else {
        await iwalkcvService.createIlha(payload);
      }
      setDialogOpen(false);
      await load();
    } catch (e) {
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActivo = async (ilha: Ilha) => {
    try {
      await iwalkcvService.toggleIlhaActivo(ilha.id, ilha.activo === 1 ? 0 : 1);
      await load();
    } catch (e) {
      console.error(e);
    }
  };

  const openDelete = (id: number) => {
    setDeletingId(id);
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (deletingId === null) return;
    setDeleting(true);
    try {
      await iwalkcvService.deleteIlha(deletingId);
      setDeleteDialogOpen(false);
      await load();
    } catch (e) {
      console.error(e);
    } finally {
      setDeleting(false);
    }
  };

  const handleNomeChange = (nome: string) => {
    setForm((prev) => ({
      ...prev,
      nome,
      slug: editingId === null ? toSlug(nome) : prev.slug,
    }));
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight={700}>Ilhas</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
          Nova Ilha
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Nome</TableCell>
              <TableCell>Slug</TableCell>
              <TableCell>Activo</TableCell>
              <TableCell align="right">Acções</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={28} />
                </TableCell>
              </TableRow>
            ) : ilhas.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  Nenhuma ilha encontrada.
                </TableCell>
              </TableRow>
            ) : (
              ilhas.map((ilha) => (
                <TableRow key={ilha.id} hover>
                  <TableCell>{ilha.id}</TableCell>
                  <TableCell>{ilha.nome}</TableCell>
                  <TableCell>
                    <Chip label={ilha.slug} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell>
                    <Switch
                      checked={ilha.activo === 1}
                      onChange={() => handleToggleActivo(ilha)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Editar">
                      <IconButton size="small" onClick={() => openEdit(ilha)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Eliminar">
                      <IconButton size="small" color="error" onClick={() => openDelete(ilha.id)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create / Edit Dialog */}
      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="xs" fullWidth>
        <DialogTitle>{editingId !== null ? 'Editar Ilha' : 'Nova Ilha'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          <TextField
            label="Nome"
            value={form.nome}
            onChange={(e) => handleNomeChange(e.target.value)}
            error={!!formErrors.nome}
            helperText={formErrors.nome}
            fullWidth
            autoFocus
          />
          <TextField
            label="Slug"
            value={form.slug}
            onChange={(e) => setForm((p) => ({ ...p, slug: e.target.value }))}
            error={!!formErrors.slug}
            helperText={formErrors.slug}
            fullWidth
          />
          <FormControlLabel
            control={
              <Switch
                checked={form.activo}
                onChange={(e) => setForm((p) => ({ ...p, activo: e.target.checked }))}
              />
            }
            label="Activo"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={saving}>Cancelar</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? <CircularProgress size={18} /> : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Confirmar eliminação</DialogTitle>
        <DialogContent>
          <Typography>Tem a certeza que pretende eliminar esta ilha? Esta acção é irreversível.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>Cancelar</Button>
          <Button variant="contained" color="error" onClick={handleDelete} disabled={deleting}>
            {deleting ? <CircularProgress size={18} /> : 'Eliminar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default IWalkCVIlhas;
